import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData, upsertShortstay } from "../../../../redux/shortstaysSlice";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const T = {
  bg:       "#FFFFFF",
  text:     "#0A0A0A",
  textSoft: "#444444",
  muted:    "#9A9A9A",
  border:   "#EBEBEB",
  gold:     "#C9A84C",
  goldDark: "#9A7A2E",
  black:    "#0A0A0A",
  sans:     "'Montserrat', sans-serif",
};

const ListingDirection: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const currentListing = useSelector((state: RootState) => state.shortstays.currentListing);
  const [direction, setDirection] = useState("");
  const [editing, setEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId]);

  useEffect(() => {
    if (currentListing && currentListing.listingId === listingId && currentListing.direction) {
      setDirection(currentListing.direction);
    }
  }, [currentListing, listingId]);

  const handleSave = async () => {
    if (!listingId || !hasChanges) return;
    await dispatch(upsertShortstay({ listingId, data: { direction } }));
    setEditing(false);
    setHasChanges(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div style={{ fontFamily: T.sans, color: T.text, maxWidth: 560, margin: "0 auto", position: "relative" }}>

      {/* SUCCESS TOAST */}
      {saved && (
        <div
          style={{
            position: "fixed",
            bottom: 32,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            background: "#FFFFFF",
            border: "1px solid #D4EDDA",
            borderRadius: 12,
            padding: "14px 22px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
            minWidth: 220,
          }}
        >
          <CheckCircleOutlineIcon sx={{ color: "#22883F" }} />
          <div>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#22883F", margin: 0 }}>Successfully saved</p>
            <p style={{ fontSize: 11, color: T.muted, margin: "2px 0 0" }}>Direction updated</p>
          </div>
        </div>
      )}

      {/* heading */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <p style={{ fontSize: 9, color: T.muted, letterSpacing: "0.13em", textTransform: "uppercase", margin: "0 0 6px" }}>
            Property
          </p>
          <h2 style={{ fontSize: 20, fontWeight: 500, color: T.black, margin: 0 }}>Getting there</h2>
          <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
            How should guests reach your place?
          </p>
        </div>
        <button
          onClick={() => setEditing(true)}
          style={{
            background: "transparent",
            border: `1px solid ${T.border}`,
            borderRadius: 10,
            padding: "9px 16px",
            fontSize: 12,
            fontWeight: 500,
            fontFamily: T.sans,
            color: T.black,
            cursor: "pointer",
            transition: "all .15s",
            whiteSpace: "nowrap",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.black; e.currentTarget.style.background = "#F5F5F5"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "transparent"; }}
        >
          Edit
        </button>
      </div>

      {/* display box — flat, no wrap */}
      <div
        style={{
          padding: 20,
          borderRadius: 10,
          border: `1px solid ${T.border}`,
          backgroundColor: "#FAFAFA",
          minHeight: 120,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        {direction ? (
          <p style={{ color: T.textSoft, lineHeight: 1.6, fontSize: 13, margin: 0 }}>{direction}</p>
        ) : (
          <p style={{ color: T.muted, fontStyle: "italic", fontSize: 13, maxWidth: 400, margin: 0 }}>
            No direction provided yet.
          </p>
        )}
      </div>

      {/* EDIT MODAL — flat, no 3d shadow, white bg */}
      {editing && (
        <>
          <div
            onClick={() => setEditing(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(6px)",
              zIndex: 998,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              width: "90%",
              maxWidth: 560,
              transform: "translate(-50%, -50%)",
              background: T.bg,
              borderRadius: 14,
              padding: 24,
              zIndex: 999,
              border: `1px solid ${T.border}`,
            }}
          >
            <label style={{ fontSize: 11, fontWeight: 500, color: T.textSoft, letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>
              Directions
            </label>
            <textarea
              placeholder="Type clear instructions or landmarks to guide guests..."
              value={direction}
              onChange={(e) => {
                setDirection(e.target.value);
                setHasChanges(true);
              }}
              style={{
                width: "100%",
                minHeight: 120,
                padding: "11px 14px",
                borderRadius: 10,
                border: `1px solid ${T.border}`,
                backgroundColor: "#FAFAFA",
                color: T.black,
                fontFamily: T.sans,
                fontSize: 13,
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
                marginBottom: 20,
                transition: "border-color .15s",
              }}
              onFocus={(e) => (e.target.style.borderColor = T.black)}
              onBlur={(e) => (e.target.style.borderColor = T.border)}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setEditing(false)}
                style={{
                  padding: "11px 24px",
                  background: "transparent",
                  border: `1px solid ${T.border}`,
                  color: T.black,
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: T.sans,
                  cursor: "pointer",
                  transition: "all .15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.black; e.currentTarget.style.background = "#F5F5F5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.background = "transparent"; }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!hasChanges}
                style={{
                  padding: "11px 28px",
                  background: hasChanges ? T.black : "#F0F0F0",
                  color: hasChanges ? "#FFFFFF" : T.muted,
                  border: "none",
                  borderRadius: 10,
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: T.sans,
                  letterSpacing: "0.04em",
                  cursor: hasChanges ? "pointer" : "not-allowed",
                  boxShadow: hasChanges ? "0 4px 14px rgba(0,0,0,0.18)" : "none",
                  transition: "all .2s ease",
                }}
              >
                Save
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListingDirection;